import React from 'react';
import AuthorizedThumbnail from './AuthorizedThumbnail';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  useTheme,
  Chip,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  InfoOutlined as InfoIcon,
  VisibilityOutlined as PreviewIcon,
  HistoryOutlined as HistoryIcon,
  LockOutlined as LockIcon,
  InsertDriveFile as FileIcon,
  Description as DefaultFileIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as WordIcon,
  TableChart as ExcelIcon,
  Image as ImageIcon,
  Audiotrack as AudioIcon,
  VideoLibrary as VideoIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon,
  TextSnippet as TextIcon,
  ContentCopy as CopyIcon,
  DriveFileMove as MoveIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
} from '@mui/icons-material';
import { formatFileSize, getFileIcon } from '../../utils/fileUtils';
import { File } from '../../types';
import { RootState } from '../../store';
import { toggleSelectItem } from '../../store/slices/uiSlice';
import DraggableItem from '../ui/DraggableItem';

export interface FileCardProps {
  file: File;
  onClick?: () => void;
  onOperation?: (operation: string, fileId: string, data?: any) => void;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  isCloudFile?: boolean;
  onImport?: () => void;
  listVariant?: 'default' | 'trash';
}

function alpha(color: string, opacity: number): string {
  return color + Math.round(opacity * 255).toString(16).padStart(2, '0');
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  onClick,
  onOperation,
  isSelected,
  onSelectionChange,
  listVariant = 'default',
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isTrash = listVariant === 'trash';
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { isSelectionMode, selectedItems } = useSelector((state: RootState) => state.ui);
  const [isHovered, setIsHovered] = React.useState(false);

  // Handler for menu icon click
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    event.currentTarget.blur();
    setAnchorEl(event.currentTarget);
  };

  // Handler for right-click (context menu) on card
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.blur();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCardClick = (event: React.MouseEvent) => {
    if (isSelectionMode) {
      dispatch(toggleSelectItem({ itemId: file.id, itemType: 'file' }));
    } else {
      onClick?.();
    }
  };

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(toggleSelectItem({ itemId: file.id, itemType: 'file' }));
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOperation?.('star', file.id, !file.isStarred);
  };

  // Function to determine file type for the chip
  const getFileType = () => {
    if (!file.mimeType) return 'File';
    if (file.mimeType.includes('image')) return 'Image';
    if (file.mimeType.includes('video')) return 'Video';
    if (file.mimeType.includes('audio')) return 'Audio';
    if (file.mimeType.includes('pdf')) return 'PDF';
    if (file.mimeType.includes('spreadsheet') || file.mimeType.includes('excel')) return 'Spreadsheet';
    if (file.mimeType.includes('document') || file.mimeType.includes('word')) return 'Document';
    if (file.mimeType.includes('presentation') || file.mimeType.includes('powerpoint')) return 'Presentation';
    return 'File';
  };

  // Get the file's extension for display
  const getFileExtension = () => {
    const parts = file.name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  };

  // Get chip color based on file type
  const getChipColor = () => {
    const type = getFileType();
    switch (type) {
      case 'Image': return { bg: '#E3F2FD', color: '#1976D2' };
      case 'Video': return { bg: '#E8F5E9', color: '#2E7D32' };
      case 'Audio': return { bg: '#F3E5F5', color: '#7B1FA2' };
      case 'PDF': return { bg: '#FFEBEE', color: '#C62828' };
      case 'Spreadsheet': return { bg: '#E0F2F1', color: '#00796B' };
      case 'Document': return { bg: '#E8EAF6', color: '#3F51B5' };
      case 'Presentation': return { bg: '#FFF3E0', color: '#E65100' };
      default: return { bg: '#ECEFF1', color: '#546E7A' };
    }
  };

  const chipColors = getChipColor();
  const fileExtension = getFileExtension();
  const fileType = getFileType();
  const formattedDate = new Date(file.updatedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  
  const formattedSize = formatFileSize(file.size);

  const handleDragStart = (e: React.DragEvent) => {
    if (isSelectionMode || isTrash) return;
    
    const dragData = {
      id: file.id,
      type: 'file',
      item: {
        ...file,
        path: file.path || ''  // Ensure path is always a string
      }
    };
    
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Determine file icon based on file type
  const getFileIcon = () => {
    const fileType = file.mimeType || file.mimetype || '';
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    
    // Document types
    if (fileType.includes('pdf') || extension === 'pdf') {
      return <PdfIcon sx={{ fontSize: 40, color: '#E44D26' }} />;
    }
    if (fileType.includes('word') || extension === 'doc' || extension === 'docx') {
      return <WordIcon sx={{ fontSize: 40, color: '#2B579A' }} />;
    }
    if (fileType.includes('spreadsheet') || extension === 'xls' || extension === 'xlsx') {
      return <ExcelIcon sx={{ fontSize: 40, color: '#217346' }} />;
    }
    if (fileType.includes('text') || extension === 'txt') {
      return <TextIcon sx={{ fontSize: 40, color: '#7e57c2' }} />;
    }
    
    // Media types
    if (fileType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return <AuthorizedThumbnail id={file.id} name={file.name}/>;
    }
    if (fileType.includes('audio') || ['mp3', 'wav', 'ogg', 'flac'].includes(extension)) {
      return <AudioIcon sx={{ fontSize: 40, color: '#FF7043' }} />;
    }
    if (fileType.includes('video') || ['mp4', 'webm', 'mov', 'avi'].includes(extension)) {
      return <VideoIcon sx={{ fontSize: 40, color: '#FF3D00' }} />;
    }
    
    // Archive types
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return <ArchiveIcon sx={{ fontSize: 40, color: '#8D6E63' }} />;
    }
    
    // Code types
    if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'py', 'java', 'php', 'c', 'cpp'].includes(extension)) {
      return <CodeIcon sx={{ fontSize: 40, color: '#42A5F5' }} />;
    }
    
    // Default
    return <DefaultFileIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />;
  };

  return (
    <DraggableItem
      id={file.id}
      type="file"
      item={{
        ...file,
        path: file.path || ''  // Ensure path is always a string
      }}
      disableDrag={isSelectionMode || isTrash}
    >
      <Card 
        draggable={!isTrash}
        onDragStart={isTrash ? undefined : handleDragStart}
        onContextMenu={handleContextMenu}
        elevation={0}
        sx={{ 
          position: 'relative',
          height: '100%',
          transition: 'all 0.3s ease',
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
          borderColor: isSelected ? theme.palette.primary.main : 'divider',
          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            borderColor: isSelected ? theme.palette.primary.main : theme.palette.primary.light,
          },
          overflow: 'visible',
          borderRadius: 2
        }}
      >
        {file.isShared && (
          <Badge
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              zIndex: 2,
            }}
            badgeContent={
              <Tooltip title="Shared with others">
                <ShareIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              </Tooltip>
            }
          />
        )}

        {isSelectionMode && (
          <Checkbox
            checked={isSelected}
            onClick={handleCheckboxClick}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              borderRadius: '50%',
              padding: '4px',
              '& .MuiSvgIcon-root': {
                fontSize: '20px',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          />
        )}

        <IconButton
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
          onClick={handleMenuOpen}
          size="small"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <CardActionArea
          onClick={handleCardClick}
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'stretch',
            height: '100%',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            py: 3,
            flexGrow: 1,
            position: 'relative',
            backgroundColor: chipColors.bg + '40', // Add 25% opacity
          }}>
            <Box sx={{ 
              position: 'relative', 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}>
              {getFileIcon()}
              <Chip
                label={fileExtension}
                size="small"
                sx={{
                  backgroundColor: chipColors.bg,
                  color: chipColors.color,
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: 24,
                  borderRadius: 1,
                  border: `1px solid ${chipColors.color}20`,
                }}
              />
            </Box>
          </Box>

          <CardContent 
            sx={{ 
              p: 1.5, 
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                <Tooltip title={file.name} placement="top">
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {fileType} • {formattedSize}
                </Typography>
                
                {!isTrash && (
                <IconButton
                  size="small"
                  onClick={handleStarClick}
                  sx={{
                    color: file.isStarred ? 'warning.main' : 'action.disabled',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      color: file.isStarred ? 'warning.main' : 'warning.light',
                    },
                  }}
                >
                  {file.isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
                )}
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Modified: {formattedDate}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {isTrash ? (
            <>
              <MenuItem onClick={() => {
                handleMenuClose();
                onOperation?.('preview', file.id);
              }}>
                <ListItemIcon>
                  <PreviewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Open" />
              </MenuItem>
              <MenuItem onClick={() => {
                handleMenuClose();
                onOperation?.('restore', file.id);
              }}>
                <ListItemIcon>
                  <RestoreFromTrashIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Restore" />
              </MenuItem>
              <MenuItem onClick={() => {
                handleMenuClose();
                onOperation?.('delete', file.id);
              }}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary="Delete permanently" primaryTypographyProps={{ color: 'error' }} />
              </MenuItem>
            </>
          ) : (
            <>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('preview', file.id);
          }}>
            <ListItemIcon>
              <PreviewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('download', file.id);
          }}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Download" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('share', file.id);
          }}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Share" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('rename', file.id);
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Rename" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('copy', file.id);
          }}>
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Make a copy" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('move', file.id);
          }}>
            <ListItemIcon>
              <MoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Move" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('star', file.id, !file.isStarred);
          }}>
            <ListItemIcon>
              {file.isStarred ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={file.isStarred ? 'Remove from favourites' : 'Add to favourites'} />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('properties', file.id);
          }}>
            <ListItemIcon>
              <InfoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Properties" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => {
            handleMenuClose();
            onOperation?.('delete', file.id);
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete" primaryTypographyProps={{ color: 'error' }} />
          </MenuItem>
            </>
          )}
        </Menu>
      </Card>
    </DraggableItem>
  );
};

export default FileCard; 